import React, { useState, useEffect } from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, MapPin } from 'lucide-react';
import InnerPageLayout from '../components/InnerPageLayout';
import { useBusinessInfo } from '../hooks/useBusinessInfo';
import { CMSComponentRenderer } from '../components/cms';
import ContentRenderer from '../components/ContentRenderer';
import StructuredContentRenderer from '../components/StructuredContentRenderer';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DynamicCMSPage = () => {
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [pageComponents, setPageComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const primaryColor = 'var(--theme-primary)';

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const path = location.pathname;
        const searchParams = new URLSearchParams(location.search);
        const isPreview = searchParams.get('preview') === 'true';
        
        // Use preview endpoint if preview mode, otherwise use public endpoint
        const endpoint = isPreview 
          ? `${API_BASE_URL}/api/cms/pages${path}?preview=true`
          : `${API_BASE_URL}/api/public/cms/pages${path}`;
        
        const headers = {};
        if (isPreview) {
          // Add auth token for preview mode
          const token = localStorage.getItem('token');
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        }
        
        const response = await axios.get(endpoint, { headers });
        
        if (response.data.success && response.data.page) {
          setPageData(response.data.page);
          setNotFound(false);
          
          // Fetch component details if page has components
          if (response.data.page.components && response.data.page.components.length > 0) {
            const componentsResponse = await axios.get(`${API_BASE_URL}/api/public/cms/components`);
            const allComponents = componentsResponse.data.components || [];
            
            // Map component IDs to full component data with page-specific config overrides
            const resolvedComponents = response.data.page.components.map(pageComponent => {
              // Support both old format (string ID) and new format (object with component_id and config)
              const componentId = typeof pageComponent === 'string' ? pageComponent : pageComponent.component_id;
              const pageConfig = typeof pageComponent === 'object' ? pageComponent.config : {};
              
              const baseComponent = allComponents.find(c => c.id === componentId);
              if (!baseComponent) return null;
              
              // Merge base component config with page-specific config overrides
              return {
                ...baseComponent,
                config: { ...baseComponent.config, ...pageConfig }
              };
            }).filter(c => c); // Filter out any not found
            
            setPageComponents(resolvedComponents);
          } else {
            setPageComponents([]);
          }
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching CMS page:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [location.pathname]);

  // Redirect to home if page not found
  if (notFound && !loading) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
      </div>
    );
  }

  if (!pageData) {
    return <Navigate to="/" replace />;
  }

  // Hero content for InnerPageLayout
  const heroContent = (
    <div className="text-center lg:text-left">
      <h1 className="font-bold text-gray-900 mb-4" style={{ fontSize: '1.25rem' }}>
        {pageData.title}
      </h1>
      {pageData.badge_text && (
        <div className="mb-4">
          <span 
            className="inline-flex items-center px-4 py-2 rounded-md font-semibold text-white" 
            style={{ backgroundColor: primaryColor, fontSize: '1rem' }}
          >
            {pageData.badge_text}
          </span>
        </div>
      )}
      {pageData.description && (
        <p className="text-gray-600 leading-relaxed" style={{ fontSize: '1rem' }}>
          {pageData.description}
        </p>
      )}
    </div>
  );

  // Render main content
  const renderMainContent = () => {
    if (pageData.main_content) {
      // First, try to parse as JSON
      let contentObj = null;
      try {
        if (typeof pageData.main_content === 'string') {
          contentObj = JSON.parse(pageData.main_content);
        } else if (typeof pageData.main_content === 'object') {
          contentObj = pageData.main_content;
        }
        
        // If it's structured JSON with sections array, use StructuredContentRenderer
        if (contentObj && contentObj.sections && Array.isArray(contentObj.sections)) {
          return (
            <StructuredContentRenderer 
              content={contentObj} 
              primaryColor={primaryColor} 
            />
          );
        }
      } catch (e) {
        // Not JSON, continue to check if it's HTML
      }
      
      // Check if content is HTML (contains HTML tags)
      const isHTML = /<[^>]+>/.test(pageData.main_content);
      
      if (isHTML) {
        // Render as HTML with comprehensive styling
        return (
          <div className="html-content-wrapper">
            <div dangerouslySetInnerHTML={{ __html: pageData.main_content }} />
            {renderStyles()}
          </div>
        );
      } else {
        // Render as plain text with paragraphs
        const paragraphs = pageData.main_content.split('\n\n').filter(p => p.trim());
        return (
          <section className="bg-gray-50 py-6">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="space-y-4">
                {paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-base text-gray-600 leading-relaxed text-center">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </section>
        );
      }
    }
    return (
      <section className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600">
            {pageData.description || 'Welcome to our page.'}
          </p>
        </div>
      </section>
    );
  };
  
  // Render styles (extracted for reuse)
  const renderStyles = () => (
    // eslint-disable-next-line react/no-unknown-property
    <style jsx>{`
      .html-content-wrapper {
        background-color: #f9fafb;
      }
      
      /* Content Sections */
      .html-content-wrapper :global(.content-section) {
        max-width: 72rem;
        margin: 0 auto;
        padding: 2rem 1rem;
      }
      
      /* Typography - Matching website theme */
      .html-content-wrapper :global(.section-heading),
      .html-content-wrapper :global(h2) {
        font-size: 1.25rem;
        font-weight: 700;
        color: #111827;
        margin-bottom: 1.5rem;
        text-align: center;
      }
      
      .html-content-wrapper :global(.subsection-heading),
      .html-content-wrapper :global(h3) {
        font-size: 1.125rem;
        font-weight: 600;
        color: #374151;
        margin: 1.5rem 0 1rem 0;
      }
      
      .html-content-wrapper :global(.item-heading),
      .html-content-wrapper :global(h4) {
        font-size: 1rem;
        font-weight: 600;
        color: #111827;
        margin: 1rem 0 0.5rem 0;
      }
      
      .html-content-wrapper :global(p) {
        color: #4b5563;
        line-height: 1.75;
        margin-bottom: 1rem;
        font-size: 1rem;
      }
      
      /* Benefit Cards */
      .html-content-wrapper :global(.benefit-cards) {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 1.5rem;
        margin: 2rem 0;
        max-width: 72rem;
        margin-left: auto;
        margin-right: auto;
        padding: 0 1rem;
      }
      
      .html-content-wrapper :global(.benefit-card) {
        background: white;
        padding: 1.5rem;
        border-radius: 0.75rem;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        text-align: center;
      }
      
      .html-content-wrapper :global(.card-icon) {
        font-size: 2.5rem;
        margin-bottom: 1rem;
      }
      
      .html-content-wrapper :global(.card-title) {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
        margin-bottom: 0.75rem;
      }
      
      .html-content-wrapper :global(.card-description) {
        color: #6b7280;
        font-size: 0.875rem;
        margin-bottom: 0.75rem;
        line-height: 1.5;
      }
      
      .html-content-wrapper :global(.card-badge) {
        display: inline-block;
        padding: 0.25rem 0.75rem;
        background-color: #dbeafe;
        color: #1e40af;
        font-size: 0.75rem;
        font-weight: 500;
        border-radius: 9999px;
      }
      
      /* Colored Boxes */
      .html-content-wrapper :global(.colored-box) {
        padding: 1.5rem;
        border-radius: 0.75rem;
        border-left: 4px solid;
        margin: 2rem auto;
        max-width: 72rem;
      }
      
      .html-content-wrapper :global(.colored-box.bg-red) {
        background-color: #fef2f2;
        border-color: #ef4444;
      }
      
      .html-content-wrapper :global(.colored-box.bg-blue) {
        background-color: #eff6ff;
        border-color: #3b82f6;
      }
      
      .html-content-wrapper :global(.colored-box.bg-amber) {
        background-color: #fffbeb;
        border-color: #f59e0b;
      }
      
      .html-content-wrapper :global(.colored-box.bg-green) {
        background-color: #f0fdf4;
        border-color: #10b981;
      }
      
      .html-content-wrapper :global(.box-title) {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }
      
      .html-content-wrapper :global(.bg-red .box-title) {
        color: #7f1d1d;
      }
      
      .html-content-wrapper :global(.bg-blue .box-title) {
        color: #1e3a8a;
      }
      
      .html-content-wrapper :global(.bg-amber .box-title) {
        color: #78350f;
      }
      
      .html-content-wrapper :global(.bg-green .box-title) {
        color: #14532d;
      }
      
      .html-content-wrapper :global(.box-subtitle) {
        font-size: 0.875rem;
        margin: 0;
      }
      
      .html-content-wrapper :global(.bg-red .box-subtitle) {
        color: #991b1b;
      }
      
      .html-content-wrapper :global(.bg-blue .box-subtitle) {
        color: #1e40af;
      }
      
      .html-content-wrapper :global(.bg-amber .box-subtitle) {
        color: #92400e;
      }
      
      .html-content-wrapper :global(.bg-green .box-subtitle) {
        color: #166534;
      }
      
      /* Two Column Layout */
      .html-content-wrapper :global(.two-columns) {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem;
        margin: 2rem auto;
        max-width: 72rem;
        padding: 0 1rem;
      }
      
      .html-content-wrapper :global(.column) {
        background: white;
        padding: 1.5rem;
        border-radius: 0.75rem;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      }
      
      .html-content-wrapper :global(.column-title) {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
        margin-bottom: 1rem;
      }
      
      .html-content-wrapper :global(.item-list) {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      
      .html-content-wrapper :global(.list-item) {
        border-left: 4px solid var(--theme-primary, #3b82f6);
        padding-left: 1rem;
      }
      
      .html-content-wrapper :global(.item-name) {
        font-weight: 600;
        color: #111827;
        font-size: 1rem;
        margin-bottom: 0.25rem;
      }
      
      .html-content-wrapper :global(.item-desc) {
        color: #6b7280;
        font-size: 0.875rem;
        line-height: 1.5;
        margin: 0;
      }
      
      /* Lists */
      .html-content-wrapper :global(ul),
      .html-content-wrapper :global(ol) {
        margin-left: 1.5rem;
        margin-bottom: 1rem;
        color: #4b5563;
      }
      
      .html-content-wrapper :global(li) {
        margin-bottom: 0.5rem;
        line-height: 1.75;
      }
      
      /* Callout Box */
      .html-content-wrapper :global(.callout-box) {
        background-color: rgba(59, 130, 246, 0.1);
        border: 2px solid rgba(59, 130, 246, 0.3);
        padding: 1.5rem;
        border-radius: 0.75rem;
        margin: 2rem auto;
        max-width: 72rem;
        text-align: center;
      }
      
      .html-content-wrapper :global(.callout-box p) {
        font-weight: 500;
        color: var(--theme-primary, #3b82f6);
        font-size: 1.125rem;
        margin: 0;
      }
      
      /* Info Box */
      .html-content-wrapper :global(.info-box) {
        background-color: #eff6ff;
        border-left: 4px solid #3b82f6;
        padding: 1rem;
        margin: 1.5rem auto;
        border-radius: 0.5rem;
        max-width: 72rem;
      }
      
      .html-content-wrapper :global(.info-box p) {
        margin: 0;
        color: #1e40af;
      }
      
      /* Sections with specific classes */
      .html-content-wrapper :global(section) {
        max-width: 72rem;
        margin: 0 auto;
        padding: 2rem 1rem;
      }
      
      /* Text Formatting */
      .html-content-wrapper :global(strong) {
        font-weight: 600;
        color: #111827;
      }
      
      .html-content-wrapper :global(em) {
        font-style: italic;
      }
      
      .html-content-wrapper :global(a) {
        color: var(--theme-primary, #3b82f6);
        text-decoration: underline;
      }
      
      .html-content-wrapper :global(a:hover) {
        color: var(--theme-primary-hover, #2563eb);
      }
    `}</style>
  );

  // Render components after main content
  const renderPageComponents = () => {
    if (pageComponents.length === 0) return null;
    
    return pageComponents.map((component, index) => (
      <CMSComponentRenderer
        key={`${component.id}-${index}`}
        componentType={component.type}
        config={component.config}
      />
    ));
  };

  return (
    <InnerPageLayout 
      heroContent={heroContent} 
      customImage={pageData.hero_image} 
      layout={pageData.layout}
      imageAspect={pageData.hero_image_aspect}
    >
      <div className="bg-gray-50">
        {renderMainContent()}
        
        {/* Render all page components in order after main content */}
        {renderPageComponents()}
      </div>
    </InnerPageLayout>
  );
};

export default DynamicCMSPage;
